import {
  InitialStateModel,
  Message as WebhookMessage,
} from "@/types/inboundWebhookPayload";
import { computed, ref } from "vue";
import { Conversation, Message } from "@/types/conversation";
import { useDocumentVisibility, useEventBus, useNow } from "@vueuse/core";
import {
  MessageListScrollNotifyBus,
  MessageListScrollNotifyBusKey,
} from "@/types/MessageListScrollNotifyBus";
import { useApp } from "@/composables/use-app";
import { getHumanChannel } from "@/util/formatting";
import { TransferChannelOption } from "@/components/modals/TransferConversationModal.vue";
import { useWidgetConfig } from "@/composables/widgetConfig";
import * as EventTypes from "@/shared/eventTypes";

const isReady = ref(false);
const messages = ref<Array<Message>>([]);
const conversation = ref<Conversation>();
const unreadMessages = ref(0);
const isWidgetOpen = ref(false);
const visitorInfo = ref<{ [key: string]: string }>();

const allowNewMessagesNMinutesAfterTransfer = 10;

const toLocalMessage = (message: WebhookMessage) => {
  return {
    messageId: message.messageId ?? "",
    acknowledged: true,
    timestamp: message.timestamp ? Date.parse(message.timestamp) : new Date(),
    data: message.data,
    conversationId: message.conversationId,
  } as Message;
};

const closeWidget = () => {
  isWidgetOpen.value = false;
};

const openWidget = () => {
  isWidgetOpen.value = true;
};

export function useConversation() {
  const { playChime } = useApp();
  const visible = useDocumentVisibility();
  const scrollNotifier = useEventBus<MessageListScrollNotifyBus>(
    MessageListScrollNotifyBusKey
  );
  const { configuration, getSurveyFieldIdsByChannel } = useWidgetConfig();
  const now = useNow();

  const setStarted = (id: string) => {
    if (conversation.value) {
      conversation.value.isStarted = true;
      conversation.value.isEnded = false;
      conversation.value.isTransferred = false;
      conversation.value.id = id;
    } else {
      console.warn("Couldn't set started, no conversation object in state");
    }
  };

  const isTransferredAndWithinNewMessageThreshold = computed(() => {
    if (!conversation.value?.isTransferred) {
      return false;
    }

    const transferEvent = messages.value.filter(
      (i) =>
        i.conversationId === conversation.value?.id &&
        i.data.eventType === "conversation_transferred"
    );

    if (!transferEvent.length) {
      return false;
    }

    const transferTime = transferEvent[0].timestamp;

    return (
      now.value.getTime() - transferTime <
      allowNewMessagesNMinutesAfterTransfer * 60_000
    );
  });

  const setIsReady = () => {
    isReady.value = true;

    window.parent.postMessage(
      {
        type: EventTypes.WIDGET_LOADED,
      },
      "*"
    );
  };

  const setEnded = () => {
    if (conversation.value) {
      conversation.value.isEnded = true;
    } else {
      console.warn("Couldn't set ended, no conversation object in state");
    }
  };

  const setTransferred = () => {
    if (conversation.value) {
      conversation.value.isTransferred = true;
    } else {
      console.warn("Couldn't set transferred, no conversation object in state");
    }
  };

  const setInitialState = (state: InitialStateModel) => {
    conversation.value = {
      isStarted: state.conversation.isStarted,
      isTransferred: state.conversation.isTransferred,
      isEnded: state.conversation.isEnded,
      id: state.currentConversationId,
    };

    messages.value = state.messages.map<Message>((message) => {
      return toLocalMessage(message);
    });

    isWidgetOpen.value = state.isWidgetOpen;
    visitorInfo.value = state.visitorInfo;
  };

  const setVisitorInfo = (info: { [key: string]: string }) => {
    visitorInfo.value = info;
  };

  const upsertMessage = (message: WebhookMessage) => {
    if (
      message.data?.eventType === "conversation_transferred" &&
      conversation.value
    ) {
      conversation.value.isTransferred = true;
    }

    if (messages.value.some((i) => i.messageId === message.messageId)) {
      messages.value = messages.value.map<Message>((i) => {
        if (i.messageId === message.messageId) {
          return toLocalMessage(message);
        }

        return i;
      });
    } else {
      messages.value.push(toLocalMessage(message));
    }

    if (
      visible.value === "hidden" &&
      (message.data?.authorType === "agent" ||
        message.data?.authorType === "system")
    ) {
      playChime();
    }
  };

  const insertPendingMessage = (messageId: string, text: string) => {
    messages.value.push({
      messageId: messageId,
      acknowledged: false,
      timestamp: new Date().getTime(),
      data: {
        text: text,
        eventType: "reply",
        authorType: "client",
        author: {
          name: undefined,
        },
      },
    });

    // notifyScroll();
  };

  const transferChannelOptions = computed(() => {
    return configuration.value?.channels
      ?.filter((i) => i.offerTransfer)
      .map<TransferChannelOption>((i) => {
        let suggestionAddress: string | undefined;

        const fields = getSurveyFieldIdsByChannel(i.channel);
        if (
          fields.length &&
          visitorInfo.value &&
          fields.some((field) => field in visitorInfo.value!)
        ) {
          const filter = fields.filter(
            (field) =>
              field in visitorInfo.value! && visitorInfo.value![field]?.trim()
          );
          suggestionAddress = visitorInfo.value[filter[0]];
        }

        return {
          channel: i.channel,
          channelDescription: getHumanChannel(i.channel, true),
          suggestionAddress,
        } as TransferChannelOption;
      });
  });

  return {
    upsertMessage,
    isReady,
    unreadMessages,
    insertPendingMessage,
    transferChannelOptions,
    isTransferredAndWithinNewMessageThreshold,

    setInitialState,
    setStarted,
    setEnded,
    setVisitorInfo,
    setTransferred,
    setIsReady,

    closeWidget,
    openWidget,

    messages: computed(() => messages.value),
    conversation: computed(() => conversation.value),
    isWidgetOpen: computed(() => isWidgetOpen.value),
    visitorInfo: computed(() => visitorInfo.value),
  };
}
